import React, { useEffect, useMemo } from 'react';
import { DataTable } from '@/components';
import { GlassDataTable } from '@/components/admin/redesign';
import { useResidentTableColumns } from './ResidentDataTableConfig';
import { 
  IResidentListResponse, 
  IResidentTableRow,
  IFilterModel,
  ISortModel,
  IPaginationModel
} from '@/types/admin/residentList';
import { IDataTableResponse } from '@/types/admin/dataTable';

interface ResidentDataTableProps {
  data: IResidentListResponse;
  isLoading: boolean;
  // Optional pre-normalized rows (preferred). If provided, component won't re-normalize.
  rows?: IResidentTableRow[];
  searchQuery?: string; // optional, use context fallback
  onSearchChange?: (query: string) => void;
  filterModel?: IFilterModel;
  onFilterChange?: (filterModel: IFilterModel) => void;
  sortModel?: ISortModel[];
  onSortChange?: (sortModel: ISortModel[]) => void;
  paginationModel?: IPaginationModel;
  onPaginationChange?: (paginationModel: IPaginationModel) => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onResidentSelect: (resident: IResidentTableRow) => void;
  updateCsvData: (data: IResidentTableRow[]) => void;
  showSearch?: boolean;
  showSort?: boolean;
}

export const ResidentDataTable: React.FC<ResidentDataTableProps> = ({
  data,
  isLoading,
  rows,
  searchQuery,
  onSearchChange,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
  paginationModel,
  onPaginationChange,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onResidentSelect,
  updateCsvData,
  showSearch = false,
  showSort = true,
}) => {
  const residentTableColumns = useResidentTableColumns();

  // Rows should be pre-normalized by the hook; default to empty array
  const normalizedData = useMemo(() => rows || [], [rows]);

  // Transform data to match generic DataTable interface
  const tableData: IDataTableResponse<IResidentTableRow> = useMemo(() => ({
    rows: normalizedData,
    count: data.count,
  }), [normalizedData, data.count]);

  const handleRowSelect = (resident: IResidentTableRow, index: number) => {
    onResidentSelect(resident);
  };

  return (
    <GlassDataTable
      density="comfortable"
      style={FILL_STYLE}
      contentStyle={FILL_STYLE}
    >
      <DataTable
        data={tableData}
        columns={residentTableColumns}
        isLoading={isLoading}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search residents by name..."
        filterModel={filterModel as any}
        onFilterChange={onFilterChange as any}
        sortModel={sortModel as any}
        onSortChange={onSortChange as any}
        paginationModel={paginationModel as any}
        onPaginationChange={onPaginationChange as any}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onNextPage={onNextPage}
        onPreviousPage={onPreviousPage}
        onRowSelect={handleRowSelect}
        updateCsvData={updateCsvData}
        emptyStateType="residents"
        mobileColumnCount={2}
        showSearch={showSearch}
        showSort={showSort}
        containerStyle={FILL_STYLE}
      />
    </GlassDataTable>
  );
};

const FILL_STYLE = { flex: 1, minHeight: 0 } as const;


